/**
 * TableauReporting.js
 *
 * Composant générique pour les tableaux comparatifs du reporting mensuel BNCB.
 *
 * Supporte deux modes via la prop `mode` :
 *   - "sinistres"     : colonnes TE / ET / TOTAL / Var / %
 *   - "financier"     : colonnes nb / mt / Var NB / Var MT / % nb / % MT
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../../i18n/format';

// ── Tokens partagés ───────────────────────────────────────────────
export const C = {
    green: '#1a6b3c', ink: '#0f172a', muted: '#94a3b8',
    border: '#e2e8f0', white: '#ffffff', surface: '#f8fafc',
};

// ── Formatage montant FCFA ────────────────────────────────────────
export const fmt = (v) =>
    !v && v !== 0 ? '-'
        : new Intl.NumberFormat(localeIntl(), { minimumFractionDigits: 0 }).format(Number(v));

// ── Variation colorée ─────────────────────────────────────────────
export function VarCell({ n1, n, small = false }) {
    if (!n1 || Number(n1) === 0)
        return <td style={tdS(false, C.white, small)}>-</td>;
    const v = (Number(n) - Number(n1)) / Number(n1);
    const pos = v >= 0;
    return (
        <td style={{
            ...tdS(false, C.white, small),
            color: pos ? '#16a34a' : '#dc2626', fontWeight: 600
        }}>
            {pos ? '+' : ''}{(v * 100).toFixed(0)}%
        </td>
    );
}

// ── % du total ────────────────────────────────────────────────────
export function PctCell({ part, total, small = false }) {
    return (
        <td style={{ ...tdS(false, C.white, small), color: C.muted, fontSize: 11 }}>
            {total ? ((Number(part) / Number(total)) * 100).toFixed(1) + '%' : '-'}
        </td>
    );
}

// ── Style cellule ─────────────────────────────────────────────────
export const thS = (bg = '#1e293b', minW) => ({
    background: bg, color: C.white, padding: '7px 8px', textAlign: 'center',
    fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
    border: '1px solid #334155', whiteSpace: 'nowrap',
    ...(minW ? { minWidth: minW } : {}),
});
export const tdS = (bold = false, bg = C.white, small = false) => ({
    padding: small ? '4px 6px' : '5px 8px', textAlign: 'right',
    fontSize: small ? 11 : 12, border: `1px solid ${C.border}`, background: bg,
    fontWeight: bold ? 700 : 400, color: bold ? C.ink : '#334155',
    fontFamily: 'var(--font-main)',
});
export const tdLeft = (bold = false, bg = C.white) => ({
    ...tdS(bold, bg), textAlign: 'left', fontFamily: "'DM Sans', sans-serif",
    paddingLeft: 12, minWidth: 145,
});

// ══════════════════════════════════════════════════════════════════
//  TABLEAU COMPARATIF - Mode "sinistres" (TE / ET)
// ══════════════════════════════════════════════════════════════════
export function TableauSinistres({ lignes, total, getLabel, moisLibelle, annee, anneeRef }) {
    const { t } = useTranslation('statistiques');
    const totMoisN = total.tot_mois_n;
    const totCumulN = total.tot_cumul_n;

    return (
        <div style={{
            overflowX: 'auto', border: `1px solid ${C.border}`,
            borderTop: 'none', borderRadius: '0 0 10px 10px',
            boxShadow: '0 1px 8px rgba(0,0,0,0.05)'
        }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                    <tr>
                        <th rowSpan={3} style={{ ...thS(), minWidth: 145, verticalAlign: 'middle' }}>
                            {t('reporting.tableau.paysMembres')}
                        </th>
                        <th colSpan={8} style={thS('#166534')}>{t('reporting.tableau.moisDe', { mois: moisLibelle })}</th>
                        <th colSpan={8} style={thS('#1e3a5f')}>{t('reporting.tableau.janvierACumul', { mois: moisLibelle })}</th>
                        <th colSpan={1} style={thS('#4a1942')}>{t('reporting.tableau.finAnnee', { annee })}</th>
                    </tr>
                    <tr>
                        {[
                            ['TE', 2, '#1f4035'], ['ET', 2, '#1f4035'], ['TOTAL', 2, '#1f4035'],
                            [t('reporting.tableau.var'), 1, '#1f4035'], ['%', 1, '#1f4035'],
                            ['TE', 2, '#162d47'], ['ET', 2, '#162d47'], ['TOTAL', 2, '#162d47'],
                            [t('reporting.tableau.var'), 1, '#162d47'], ['%', 1, '#162d47'],
                            ['TE+ET', 1, '#3b1238'],
                        ].map(([l, c, b], i) => <th key={i} colSpan={c} style={thS(b)}>{l}</th>)}
                    </tr>
                    <tr>
                        {[
                            anneeRef, annee, anneeRef, annee, anneeRef, annee,
                            `${annee}/${anneeRef}`, annee,
                            anneeRef, annee, anneeRef, annee, anneeRef, annee,
                            `${annee}/${anneeRef}`, annee, 'TE+ET',
                        ].map((a, i) => (
                            <th key={i} style={{ ...thS(i < 8 ? '#1f4035' : i < 16 ? '#162d47' : '#3b1238'), fontSize: 10 }}>{a}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {lignes.map((l, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? C.white : C.surface }}>
                            <td style={tdLeft()}>{getLabel(l)}</td>
                            <td style={tdS()}>{l.te_mois_n1 || 0}</td>
                            <td style={tdS()}>{l.te_mois_n || 0}</td>
                            <td style={tdS()}>{l.et_mois_n1 || 0}</td>
                            <td style={tdS()}>{l.et_mois_n || 0}</td>
                            <td style={tdS(false, '#f0fdf4')}>{l.tot_mois_n1 || 0}</td>
                            <td style={tdS(false, '#dcfce7')}>{l.tot_mois_n || 0}</td>
                            <VarCell n1={l.tot_mois_n1} n={l.tot_mois_n} />
                            <PctCell part={l.tot_mois_n} total={totMoisN} />
                            <td style={tdS()}>{l.te_cumul_n1 || 0}</td>
                            <td style={tdS()}>{l.te_cumul_n || 0}</td>
                            <td style={tdS()}>{l.et_cumul_n1 || 0}</td>
                            <td style={tdS()}>{l.et_cumul_n || 0}</td>
                            <td style={tdS(false, '#eff6ff')}>{l.tot_cumul_n1 || 0}</td>
                            <td style={tdS(false, '#dbeafe')}>{l.tot_cumul_n || 0}</td>
                            <VarCell n1={l.tot_cumul_n1} n={l.tot_cumul_n} />
                            <PctCell part={l.tot_cumul_n} total={totCumulN} />
                            <td style={tdS(false, '#faf5ff')}>{l.tot_fda_n || 0}</td>
                        </tr>
                    ))}
                    <TotalRowSinistres total={total} totMoisN={totMoisN} totCumulN={totCumulN} />
                </tbody>
            </table>
        </div>
    );
}

function TotalRowSinistres({ total, totMoisN, totCumulN }) {
    return (
        <tr style={{ background: '#f1f5f9' }}>
            <td style={tdLeft(true, '#f1f5f9')}>TOTAL</td>
            <td style={tdS(true)}>{total.te_mois_n1}</td>
            <td style={tdS(true, '#dcfce7')}>{total.te_mois_n}</td>
            <td style={tdS(true)}>{total.et_mois_n1}</td>
            <td style={tdS(true, '#dcfce7')}>{total.et_mois_n}</td>
            <td style={tdS(true)}>{total.tot_mois_n1}</td>
            <td style={tdS(true, '#86efac')}>{total.tot_mois_n}</td>
            <VarCell n1={total.tot_mois_n1} n={total.tot_mois_n} />
            <td style={tdS(true)}>100%</td>
            <td style={tdS(true)}>{total.te_cumul_n1}</td>
            <td style={tdS(true, '#dbeafe')}>{total.te_cumul_n}</td>
            <td style={tdS(true)}>{total.et_cumul_n1}</td>
            <td style={tdS(true, '#dbeafe')}>{total.et_cumul_n}</td>
            <td style={tdS(true)}>{total.tot_cumul_n1}</td>
            <td style={tdS(true, '#93c5fd')}>{total.tot_cumul_n}</td>
            <VarCell n1={total.tot_cumul_n1} n={total.tot_cumul_n} />
            <td style={tdS(true)}>100%</td>
            <td style={tdS(true, '#e9d5ff')}>{total.tot_fda_n}</td>
        </tr>
    );
}


// ══════════════════════════════════════════════════════════════════
//  TABLEAU COMPARATIF - Mode "financier" (nb + montant)
// ══════════════════════════════════════════════════════════════════
export function TableauFinancier({ lignes, total, getLabel, moisLibelle, annee, anneeRef }) {
    const { t } = useTranslation('statistiques');
    const totNbMoisN = total.nb_mois_n;
    const totMtMoisN = Number(total.mt_mois_n);
    const totNbCumulN = total.nb_cumul_n;
    const totMtCumulN = Number(total.mt_cumul_n);

    return (
        <div style={{
            overflowX: 'auto', border: `1px solid ${C.border}`,
            borderTop: 'none', borderRadius: '0 0 10px 10px',
            boxShadow: '0 1px 8px rgba(0,0,0,0.05)'
        }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                    <tr>
                        <th rowSpan={3} style={{ ...thS(), minWidth: 145, verticalAlign: 'middle' }}>
                            {t('reporting.tableau.payeursMembres')}
                        </th>
                        <th colSpan={8} style={thS('#166534')}>{t('reporting.tableau.moisDe', { mois: moisLibelle })}</th>
                        <th colSpan={8} style={thS('#1e3a5f')}>{t('reporting.tableau.janvierACumul', { mois: moisLibelle })}</th>
                        <th colSpan={2} style={thS('#4a1942')}>{t('reporting.tableau.finAnnee', { annee })}</th>
                    </tr>
                    <tr>
                        {[
                            [`${anneeRef}`, 2, '#1f4035'], [`${annee}`, 2, '#1f4035'],
                            [t('reporting.tableau.var'), 2, '#1f4035'], ['%', 2, '#1f4035'],
                            [`${anneeRef}`, 2, '#162d47'], [`${annee}`, 2, '#162d47'],
                            [t('reporting.tableau.var'), 2, '#162d47'], ['%', 2, '#162d47'],
                            [t('reporting.tableau.nbMajuscules'), 1, '#3b1238'], [t('reporting.tableau.mtMajuscules'), 1, '#3b1238'],
                        ].map(([l, c, b], i) => <th key={i} colSpan={c} style={thS(b)}>{l}</th>)}
                    </tr>
                    <tr>
                        {Array(16).fill(null).map((_, i) => (
                            <th key={i} style={thS(i < 8 ? '#1f4035' : '#162d47')}>
                                {i % 2 === 0 ? t('reporting.tableau.nb') : t('reporting.tableau.mt')}
                            </th>
                        ))}
                        <th style={thS('#3b1238')}>{t('reporting.tableau.nb')}</th>
                        <th style={thS('#3b1238')}>FCFA</th>
                    </tr>
                </thead>
                <tbody>
                    {lignes.map((l, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? C.white : C.surface }}>
                            <td style={tdLeft()}>{getLabel(l)}</td>
                            <td style={tdS()}>{l.nb_mois_n1 || 0}</td>
                            <td style={tdS(false, '#f8fafc')}>{fmt(l.mt_mois_n1)}</td>
                            <td style={tdS()}>{l.nb_mois_n || 0}</td>
                            <td style={tdS(false, '#f0fdf4')}>{fmt(l.mt_mois_n)}</td>
                            <VarCell n1={l.nb_mois_n1} n={l.nb_mois_n} />
                            <VarCell n1={Number(l.mt_mois_n1)} n={Number(l.mt_mois_n)} />
                            <PctCell part={l.nb_mois_n} total={totNbMoisN} />
                            <PctCell part={Number(l.mt_mois_n)} total={totMtMoisN} />
                            <td style={tdS()}>{l.nb_cumul_n1 || 0}</td>
                            <td style={tdS(false, '#f0f9ff')}>{fmt(l.mt_cumul_n1)}</td>
                            <td style={tdS()}>{l.nb_cumul_n || 0}</td>
                            <td style={tdS(false, '#eff6ff')}>{fmt(l.mt_cumul_n)}</td>
                            <VarCell n1={l.nb_cumul_n1} n={l.nb_cumul_n} />
                            <VarCell n1={Number(l.mt_cumul_n1)} n={Number(l.mt_cumul_n)} />
                            <PctCell part={l.nb_cumul_n} total={totNbCumulN} />
                            <PctCell part={Number(l.mt_cumul_n)} total={totMtCumulN} />
                            <td style={tdS(false, '#faf5ff')}>{l.nb_fda_n || 0}</td>
                            <td style={tdS(false, '#faf5ff')}>{fmt(l.mt_fda_n)}</td>
                        </tr>
                    ))}
                    <TotalRowFinancier total={total} />
                </tbody>
            </table>
        </div>
    );
}

function TotalRowFinancier({ total }) {
    return (
        <tr style={{ background: '#f1f5f9' }}>
            <td style={tdLeft(true, '#f1f5f9')}>TOTAL</td>
            <td style={tdS(true)}>{total.nb_mois_n1}</td>
            <td style={tdS(true, '#e2e8f0')}>{fmt(total.mt_mois_n1)}</td>
            <td style={tdS(true, '#dcfce7')}>{total.nb_mois_n}</td>
            <td style={tdS(true, '#86efac')}>{fmt(total.mt_mois_n)}</td>
            <VarCell n1={total.nb_mois_n1} n={total.nb_mois_n} />
            <VarCell n1={Number(total.mt_mois_n1)} n={Number(total.mt_mois_n)} />
            <td style={tdS(true)}>100%</td>
            <td style={tdS(true)}>100%</td>
            <td style={tdS(true)}>{total.nb_cumul_n1}</td>
            <td style={tdS(true, '#e0e7ff')}>{fmt(total.mt_cumul_n1)}</td>
            <td style={tdS(true, '#dbeafe')}>{total.nb_cumul_n}</td>
            <td style={tdS(true, '#93c5fd')}>{fmt(total.mt_cumul_n)}</td>
            <VarCell n1={total.nb_cumul_n1} n={total.nb_cumul_n} />
            <VarCell n1={Number(total.mt_cumul_n1)} n={Number(total.mt_cumul_n)} />
            <td style={tdS(true)}>100%</td>
            <td style={tdS(true)}>100%</td>
            <td style={tdS(true, '#e9d5ff')}>{total.nb_fda_n}</td>
            <td style={tdS(true, '#e9d5ff')}>{fmt(total.mt_fda_n)}</td>
        </tr>
    );
}